import { useState, useEffect } from 'react';
import { Marker, OverlayView } from '@react-google-maps/api';
import { Card, Typography } from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import { ConnectorMarkerData } from './DashboardPage';
import { useStyles } from '../../styles/styles';
import { greenColor, redColor, yellowColor } from '../../components/main/react/App';
import { ConnectorIcon } from '../../components/other/Connectors';
import { locationDetailsTabParameters } from '../../utils/routing';
import { getChargerConnectors } from '../../api/chargeApi';
import { Connector, CONNECTOR_STATUS } from '../../types/Connector';
import { CONNECTOR_TYPE } from 'types/ConnectorType';

export const MapConnectorMarker = ({
  data,
  clusterer,
}: {
  data: ConnectorMarkerData;
  clusterer?: any;
}) => {
  const [expanded, setExpanded] = useState(false);
  const [detailedConnectors, setDetailedConnectors] = useState<Connector[]>([]);
  const [isLoadingConnectors, setIsLoadingConnectors] = useState<boolean>(false);
  const history = useHistory();

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const closeExpanded = () => setExpanded(false);

    if (expanded) {
      timeoutId = setTimeout(() => {
        document.addEventListener('click', closeExpanded);
      }, 0);
    }

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('click', closeExpanded);
    };
  }, [expanded]);

  const loadConnectorDetails = async () => {
    // If there are no charge point IDs or they are all null, return early
    if (!data.chargePointIds || !data.chargePointIds.length) return;

    setIsLoadingConnectors(true);
    try {
      // Filter out null values before making API calls
      const validChargePointIds = data.chargePointIds.filter((id) => id !== null);

      if (validChargePointIds.length === 0) {
        setDetailedConnectors([]);
        return;
      }

      const connectorPromises = validChargePointIds.map((cpId) =>
        getChargerConnectors(cpId).then((response) => response.data.data),
      );

      const results = await Promise.all(connectorPromises);
      const allConnectors = results.flat();
      setDetailedConnectors(allConnectors);
    } catch (error) {
      console.error('Failed to load connector details:', error);
    } finally {
      setIsLoadingConnectors(false);
    }
  };

  const getPinColor = () => {
    if (data.connectors.hasError || data.connectors.errorCount > 0) {
      return redColor;
    }
    if (
      data.connectors.maintenanceCount > 0 ||
      detailedConnectors.some((connector) => connector.status === 'maintenance')
    ) {
      return yellowColor;
    }
    return greenColor;
  };

  const handleMarkerClick = () => {
    setExpanded(true);
    if (detailedConnectors.length === 0 && !isLoadingConnectors) {
      loadConnectorDetails();
    }
  };

  const handleExpandedClick = (e: any, goToDetailsPage: boolean) => {
    e.stopPropagation();
    if (goToDetailsPage) {
      history.push(locationDetailsTabParameters(data.locationid));
    }
  };

  return (
    <>
      <Marker
        position={{ lat: data.lat, lng: data.lng }}
        onClick={handleMarkerClick}
        clusterer={clusterer}
        icon={{
          url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
      <svg xmlns="http://www.w3.org/2000/svg" width="45" height="28" viewBox="0 0 45 28">
        <defs>
          <filter id="dropshadow" height="130%">
            <feDropShadow dx="1" dy="2" stdDeviation="2" flood-color="grey" />
          </filter>
        </defs>
        <g filter="url(#dropshadow)">
          <path fill="${getPinColor()}" stroke="white" stroke-width="2"
            d="M11.8506 0C5.77545 0 0.850586 4.92487 0.850586 11C0.850586 17.0751
            5.77546 22 11.8506 22H19.1902L21.9361 27.7693L24.682 22H33.1302C39.2053
            22 44.1302 17.0751 44.1302 11C44.1302 4.92487 39.2053 0 33.1301 0H11.8506Z"/>
        </g>
        <text x="50%" y="58%" text-anchor="middle" fill="black" font-size="12" font-weight="bold" font-family="Arial, sans-serif">
          ${data.connectors.totalCount} | ${data.connectors.inUseCount}
        </text>
      </svg>
    `)}`,
          scaledSize: new window.google.maps.Size(45, 28),
          anchor: new window.google.maps.Point(22, 28),
        }}
      />

      {expanded && (
        <OverlayView
          position={{ lat: data.lat, lng: data.lng }}
          mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
        >
          <div
            style={{
              position: 'absolute',
              transform: 'translate(-50%, -100%)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'default',
              pointerEvents: 'all',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -70%)',
                textAlign: 'center',
                fontWeight: 'bold',
              }}
            >
              <Card
                style={{
                  padding: '16px',
                  boxShadow: 'none',
                  filter: 'drop-shadow(1px 2px 4px grey)',
                  width: '100%',
                  borderRadius: 20,
                }}
              >
                <Typography
                  onClick={(e) => handleExpandedClick(e, true)}
                  style={{
                    color: greenColor,
                    fontSize: 14,
                    fontWeight: 400,
                    whiteSpace: 'nowrap',
                    textAlign: 'left',
                    cursor: 'pointer',
                  }}
                >
                  {data.locationName}
                </Typography>
                <Typography style={{ whiteSpace: 'nowrap', fontSize: 12, textAlign: 'left' }}>
                  {data.locationAddress}
                </Typography>
                <div style={{ display: 'flex', flexDirection: 'row', marginTop: '8px' }}>
                  <div>
                    <Typography style={{ whiteSpace: 'nowrap', marginRight: '16px' }}>
                      {data.connectors.totalCount} | {data.connectors.inUseCount} |{' '}
                      {data.connectors.errorCount}
                    </Typography>
                  </div>
                  {isLoadingConnectors ? (
                    <Typography style={{ fontSize: 12 }}>Loading connectors...</Typography>
                  ) : detailedConnectors.length > 0 ? (
                    detailedConnectors.map((connector) => (
                      <div key={connector.id} style={{ marginLeft: '4px' }}>
                        <ConnectorIcon type={connector.type} status={connector.status} />
                      </div>
                    ))
                  ) : (
                    Array(data.connectors.totalCount)
                      .fill(null)
                      .map((_, index) => (
                        <div key={`placeholder-${index}`} style={{ marginLeft: '4px' }}>
                          <ConnectorIcon
                            type={CONNECTOR_TYPE.Type1}
                            status={CONNECTOR_STATUS.OFFLINE}
                          />
                        </div>
                      ))
                  )}
                </div>
              </Card>
            </div>
          </div>
        </OverlayView>
      )}
    </>
  );
};
